import { TeambinderApiService } from '@src/api';
import { useQuery } from '@tanstack/react-query';
import SettingsSubscriptionPlanCard from '../setting/SettingSubscriptionPlanCard';
import { Modal, Spin, Switch } from 'antd';
import { AxiosError } from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import FreeTrialModal from '@src/components/shared/FreeTrialModal';
type BillingCycle = 'MONTHLY' | 'YEARLY';

const colors = [
  { titleColor: '#57616B', buttonColor: '#B5BDC5' },
  { titleColor: '#3294EE' },
  { titleColor: '#24C185' },
  { titleColor: '#4B89D8' },
];

// Pricing configuration with annual discounts (10% off for yearly)
const pricingConfig = {
  FREE: { monthly: 0, yearly: 0 },
  BASIC: { monthly: 4900, yearly: Math.round(4900 * 12 * 0.9) },
  PREMIUM: { monthly: 29900, yearly: Math.round(29900 * 12 * 0.9) },
  ENTERPRISE: { monthly: 99000, yearly: Math.round(99000 * 12 * 0.9) },
};

const createPlans = (billingCycle: BillingCycle) => [
  {
    name: 'FREE',
    price: pricingConfig.FREE.monthly,
    description: '바인더가 처음이신가요?\n부담없이 모든 기능을 사용해보세요',
    features: [
      '키워드 모니터링 제공',
      '카카오 알림톡 월 4개 발송',
      '키워드 설정 10개',
      'AI 컨텐츠 분석 1개',
    ],
    billingCycle: 'INFINITE' as const,
    onSubscribe: async () => {
      await TeambinderApiService.changeSubscription({
        requestBody: {
          subscriptionPlan: 'FREE',
          billingCycle: 'INFINITE',
        },
      });
    },
  },
  {
    name: 'BASIC',
    price:
      billingCycle === 'MONTHLY'
        ? pricingConfig.BASIC.monthly
        : pricingConfig.BASIC.yearly,
    description:
      '매일 새로운 소식을 손쉽게 받아보세요.\n보다 쾌적한 환경을 제공합니다.',
    features: [
      '키워드 모니터링 제공',
      '카카오 알림톡 월 35개 발송',
      '키워드 설정 35개',
      'AI 컨텐츠 분석 30개',
    ],
    billingCycle,
    originalMonthlyPrice: pricingConfig.BASIC.monthly,
    onSubscribe: async () => {
      await TeambinderApiService.changeSubscription({
        requestBody: {
          subscriptionPlan: 'BASIC',
          billingCycle: billingCycle,
        },
      });
    },
  },
  {
    name: 'PREMIUM',
    price:
      billingCycle === 'MONTHLY'
        ? pricingConfig.PREMIUM.monthly
        : pricingConfig.PREMIUM.yearly,
    description:
      '대용량 데이터 확인이 필요하신가요?\n중견기업과 스타트업에 추천합니다',
    features: [
      '키워드 모니터링 제공',
      '카카오 알림톡 무제한 발송',
      '키워드 설정 50개',
      'AI 컨텐츠 분석 300개',
    ],
    billingCycle,
    originalMonthlyPrice: pricingConfig.PREMIUM.monthly,
    onSubscribe: async () => {
      await TeambinderApiService.changeSubscription({
        requestBody: {
          subscriptionPlan: 'PREMIUM',
          billingCycle: billingCycle,
        },
      });
    },
  },
  {
    name: 'ENTERPRISE',
    price:
      billingCycle === 'MONTHLY'
        ? pricingConfig.ENTERPRISE.monthly
        : pricingConfig.ENTERPRISE.yearly,
    description:
      '대용량 데이터 확인이 필요하신가요?\n대기업과 스타트업에 추천합니다',
    features: [
      '키워드 모니터링 제공',
      '카카오 알림톡 무제한 발송',
      '키워드 설정 50개',
      'AI 컨텐츠 분석 4,000개',
    ],
    billingCycle,
    originalMonthlyPrice: pricingConfig.ENTERPRISE.monthly,
    onSubscribe: async () => {
      await TeambinderApiService.changeSubscription({
        requestBody: {
          subscriptionPlan: 'ENTERPRISE',
          billingCycle: billingCycle,
        },
      });
    },
  },
];

let cardChangeConfirmModal: {
  destroy: () => void;
  update: (configUpdate: any) => void;
};
const TabPlans = () => {
  const navigate = useNavigate();
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('MONTHLY');

  const { data: userResponse, isLoading: isLoadingUser } = useQuery(
    ['member'],
    () => TeambinderApiService.getPersonalSettings({}),
    { suspense: false },
  );

  const { data: currentSubscription, isLoading: isLoadingSubscription } =
    useQuery(
      ['current-subscription'],
      () => TeambinderApiService.getLatestSubscription({ q: 'latest' }),
      { refetchInterval: 3000, suspense: false },
    );
  const { data: cardListData, isLoading: isLoadingCards } = useQuery(
    ['cardList'],
    TeambinderApiService.findPaymentMethods,
    { suspense: false },
  );

  const currentsSubscriptionPlanName =
    // openapi 스펙 이상함
    // @ts-ignore
    currentSubscription?.data?.plan?.subscriptionPackage
      ?.subscriptionPlan as string;

  const plans = createPlans(billingCycle);

  const onSelectPlan = async (plan: ReturnType<typeof createPlans>[number]) => {
    if (currentsSubscriptionPlanName === plan.name) {
      return;
    }
    try {
      if (plan.name !== 'FREE' && cardListData?.data?.length === 0) {
        Modal.confirm({
          title: '결제 카드 등록이 필요합니다. 카드를 등록하시겠습니까?',
          onOk: () => {
            navigate(`/app/my?tab=4&loadToss=true&plan=${plan.name}`);
          },
          okText: '카드 등록하기',
          cancelText: '취소',
        });
        return;
      }

      const planNames = ['FREE', 'BASIC', 'PREMIUM', 'ENTERPRISE'];
      const canContentDeleted =
        plan.name === 'FREE' ||
        planNames.indexOf(currentsSubscriptionPlanName) >
          planNames.indexOf(plan.name);

      const billingCycleText = billingCycle === 'YEARLY' ? '연간' : '월간';
      const actualPrice =
        billingCycle === 'YEARLY'
          ? plan.price
          : plan.originalMonthlyPrice || plan.price;
      const priceText =
        billingCycle === 'YEARLY'
          ? `${actualPrice.toLocaleString()}원/년 (월 ${Math.round(
              actualPrice / 12,
            ).toLocaleString()}원)`
          : `${actualPrice.toLocaleString()}원/월`;

      cardChangeConfirmModal = Modal.confirm({
        title: `${plan.name} 플랜 (${billingCycleText})으로 변경하시겠습니까?`,
        content: (
          <div>
            <div
              style={{
                marginBottom: '16px',
                fontSize: '16px',
                fontWeight: '600',
              }}
            >
              결제 금액: {priceText}
            </div>
            {canContentDeleted && (
              <div style={{ color: '#ff4d4f' }}>
                서비스 이용량을 초과하여 등록된 키워드는 삭제 처리됩니다.
              </div>
            )}
          </div>
        ),
        onOk: async () => {
          try {
            cardChangeConfirmModal.update({
              cancelButtonProps: {
                disabled: true,
              },
            });
            await plan.onSubscribe();
            await new Promise((resolve) => setTimeout(resolve, 5000));
            Modal.info({
              title: `${plan.name} 플랜으로 변경되었습니다.`,
              onOk: () => {
                setTimeout(() => {
                  window.location.reload();
                }, 500);
              },
            });
          } catch (e: any) {
            Modal.error({
              title:
                e.body?.message ||
                '플랜 변경에 실패했습니다. 잠시 후 다시 시도해 주세요.',
            });
          }
        },
        okText: '변경하기',
        cancelText: '취소',
      });
    } catch (e) {
      Modal.info({
        title: '구독 플랜 변경에 실패했습니다.',
      });
    }
  };

  const [searchParams] = useSearchParams();
  const selectedPlan = searchParams.get('selectedPlan');
  useEffect(() => {
    if (!selectedPlan) {
      return;
    }

    navigate(`/app/my?tab=2`, { replace: true });
    const plan = plans.find((p) => p.name === selectedPlan);
    if (plan) {
      onSelectPlan(plan);
    }
  }, [selectedPlan]);

  const isLoading = isLoadingUser || isLoadingSubscription || isLoadingCards;

  if (isLoading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      {/* Billing Cycle Toggle */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          marginBottom: '24px',
          padding: '12px 20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
        }}
      >
        <div
          style={{
            fontSize: '15px',
            fontWeight: '600',
            marginBottom: '8px',
            textAlign: 'center',
            color: '#495057',
          }}
        >
          연간 결제로{' '}
          <span style={{ color: '#28a745', fontWeight: '700' }}>
            10% 추가 할인
          </span>
          된 가격으로 바인더를 만나보세요!
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
          }}
        >
          <div
            style={{
              fontSize: '14px',
              fontWeight: '500',
              color: billingCycle === 'MONTHLY' ? '#007bff' : '#6c757d',
              transition: 'color 0.2s ease',
            }}
          >
            월간 결제
          </div>

          <Switch
            checked={billingCycle === 'YEARLY'}
            onChange={(checked) =>
              setBillingCycle(checked ? 'YEARLY' : 'MONTHLY')
            }
            size="default"
            style={{
              backgroundColor:
                billingCycle === 'YEARLY' ? '#28a745' : undefined,
            }}
          />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <div
              style={{
                fontSize: '14px',
                fontWeight: '500',
                color: billingCycle === 'YEARLY' ? '#28a745' : '#6c757d',
                transition: 'color 0.2s ease',
              }}
            >
              연간 결제
            </div>
            <div
              style={{
                backgroundColor: '#28a745',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '8px',
                fontSize: '11px',
                fontWeight: '600',
              }}
            >
              10% OFF
            </div>
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '16px',
          paddingLeft: 0,
          fontSize: '20px',
          overflow: 'scroll',
        }}
      >
        {plans.map((plan, index) => (
          <div key={plan.name} style={{ display: 'flex' }}>
            <SettingsSubscriptionPlanCard
              name={plan.name || ''}
              titleColor={colors[index].titleColor}
              buttonColor={colors[index].buttonColor}
              price={plan.price || 0}
              billingCycle={plan.billingCycle}
              originalMonthlyPrice={plan.originalMonthlyPrice}
              descriptions={plan.description?.split('\n') || []}
              features={plan.features || []}
              text={
                currentsSubscriptionPlanName === plan.name ? (
                  <b>현재 사용중</b>
                ) : (
                  '변경하기'
                )
              }
              onSelect={() => onSelectPlan(plan)}
            />
          </div>
        ))}
      </div>

      {/* 무료 체험 배너 또는 이용중 상태 */}
      {userResponse?.data?.trial?.available && (
        <div
          style={{
            marginTop: '24px',
            cursor: 'pointer',
            width: '100%',
            maxWidth: '100%',
            textAlign: 'center',
          }}
          onClick={() => setShowTrialModal(true)}
        >
          <img
            src="/img/free-trial/long.png"
            alt="무료 체험"
            style={{
              maxWidth: '100%',
              height: 'auto',
              aspectRatio: '1400 / 96',
              borderRadius: '8px',
              display: 'block',
            }}
          />
        </div>
      )}

      {userResponse?.data?.trial?.status === 'ACTIVE' && (
        <div
          style={{
            marginTop: '24px',
            padding: '20px',
            backgroundColor: '#f8f9ff',
            borderRadius: '12px',
            border: '1px solid #e6e8ff',
            width: '100%',
            textAlign: 'center',
          }}
        >
          <div
            style={{
              fontSize: '16px',
              color: '#4403B5',
              fontWeight: '600',
            }}
          >
            엔터프라이즈 무료 한달체험 이용중
          </div>
        </div>
      )}

      {/* 무료 체험 모달 */}
      <FreeTrialModal
        open={showTrialModal}
        onCancel={() => setShowTrialModal(false)}
        onOk={() => setShowTrialModal(false)}
      />
    </>
  );
};

export default TabPlans;
